/* lib/useEdgeSwipeBack.js — EL GESTO DE SALIDA ESTILO iOS (Tanda COS-H).
 *
 * UNA sola implementación para las cinco pantallas que lo estrenan (adaptiva en
 * curso, resumen de adaptiva, flashcards en curso, resumen de flashcards y
 * resultados del examen) y para el hermano de COS-38 (cambiar de tarjeta con el
 * dedo). Cero copias: quien quiera el gesto importa de aquí.
 *
 * ── LA MECÁNICA (la del "regresar" interactivo de iOS) ──────────────────────
 *   1. NACE SOLO EN EL BORDE. Un `touchstart` con `clientX <= EDGE_ZONE_PX`
 *      pertenece al gesto de salida. Uno que nace fuera de esa franja JAMÁS lo
 *      activa — y es justamente el que COS-38 sí puede reclamar.
 *      REMATE-B — DÓNDE se escucha ese `touchstart` es la mitad del asunto, y
 *      era el bug: ver EL CAPTADOR, abajo.
 *   2. SIGUE AL DEDO. Mientras arrastra, la vista se mueve con `translateX` y
 *      NADA más: cero animación de layout (la lección de `glass-card` y su
 *      `transition: all .3s`), cero cambios de clase, cero re-render de React —
 *      el transform se escribe DIRECTO en el nodo. Un arrastre no vuelve a
 *      rendear la pantalla ni una vez.
 *   3. AL SOLTAR decide el umbral: pasó COMMIT_RATIO del ancho, o fue un flick
 *      (velocidad ≥ FLICK_VELOCITY_PX_MS con recorrido mínimo) → COMMIT. Si no,
 *      resorte a su sitio y no pasa nada.
 *   4. EL COMMIT LLAMA AL MISMO HANDLER QUE EL BOTÓN de esa pantalla. No hay un
 *      `navigate` paralelo en este archivo — no existe: el hook recibe la
 *      función y la llama. Por eso el guardado de posición, el abandono de
 *      adaptiva, studyPresence/B-F2 y el resto de la doctrina de sesiones
 *      quedan intactos POR CONSTRUCCIÓN, y por eso sabotear el handler del
 *      botón rompe también el gesto (lo fija un rojo por pantalla).
 *
 * ── CONTRATOS DUROS ────────────────────────────────────────────────────────
 *   · SOLO APP. Con `IS_APP_BUILD` en falso los dos hooks devuelven el MISMO
 *     objeto vacío congelado: el contenedor no estrena ni `ref`, ni `style`, ni
 *     un handler. La cadena de render web queda idéntica, y hay un rojo que lo
 *     vigila por pantalla.
 *   · CERO LISTENERS EN `document` (doctrina S-7). Los cuatro listeners cuelgan
 *     del CAPTADOR de la pantalla, por ref de callback: se enganchan cuando el
 *     nodo entra y se sueltan cuando sale (incluido el desmontaje). Nunca en
 *     `document`, `window` ni `body`.
 *
 * ── EL CAPTADOR (REMATE-B) ─────────────────────────────────────────────────
 * El gesto "funcionaba pero costaba muchísimo dispararlo, y sólo agarraba en
 * cierto lugar" (smoke v3 de Carlos). No era el umbral: era GEOMETRÍA, y de las
 * que ningún jsdom ve.
 *
 * Los listeners colgaban del contenedor de la PANTALLA, y ese contenedor no
 * llega al filo por dos lados:
 *   · el área de contenido del chasis lleva `px-3` (App.js), o sea que los
 *     PRIMEROS 12 px de la pantalla no son de la pantalla — son del `<main>`.
 *     Un `touchstart` ahí no burbujea al contenedor (los eventos suben, no
 *     bajan): con la franja de 24 px, la franja VIVA medía 12 px, la mitad de
 *     la declarada. Y el dedo que hace "regresar" empieza justo en el filo, en
 *     los 12 px muertos.
 *   · el contenedor mide lo que mide su CONTENIDO. Debajo de él —y en las
 *     pantallas cortas ("¡Sesión Completa!", todo-dominado, mazo vacío,
 *     "respondiste todas") eso es media pantalla— vuelve a haber `<main>` y
 *     nada más. Ésa era la zona muerta VERTICAL del reporte.
 *
 * Arreglo: el gesto de salida escucha en el ANCESTRO SCROLLEABLE del contenedor
 * (`closest('main')`, el área de contenido del chasis) y sigue PINTANDO el
 * contenedor. Un solo sitio de escucha —cero doble entrega— y la franja pasa a
 * ser real de 0 a EDGE_ZONE_PX en toda la altura del contenido. Sin `<main>`
 * (un montaje suelto, jsdom) cae al propio nodo: comportamiento de siempre.
 *
 * El hermano de COS-38 NO lo usa, y es a propósito: aquel gesto tiene que nacer
 * SOBRE LA TARJETA. Ampliarle el captador convertiría cualquier arrastre
 * horizontal de la pantalla en un cambio de tarjeta.
 *   · `touchstart` y `touchmove` van NO PASIVOS y acotados a ese captador.
 *     `touchmove` porque el gesto tiene que poder cancelar el scroll cuando ya
 *     se adueñó del dedo; `touchstart` porque sin ese ancla iOS se compromete
 *     con el scroll en el primer frame y el `touchmove` llega con
 *     `cancelable:false` (WebKit #163207 — el mismo truco que ya usa
 *     `Flashcards.js` para el scroll de las caras).
 *   · CANDADO DE DIRECCIÓN. Mientras el movimiento dominante sea VERTICAL el
 *     gesto se retira y le deja el dedo al scroll. Nunca hay `preventDefault`
 *     antes de que el gesto se declare horizontal.
 *   · EL VISOR NO DECIDE NADA. `NAV.SWIPEBACK` se escribe al soltar (commit o
 *     resorte) dentro de un try/catch; si el registro fallara, el gesto sigue
 *     su camino.
 *
 * LO QUE JSDOM NO PUEDE JURAR (lo firma el dedo de Carlos en el build): que el
 * arrastre se sienta suave, que 35% y el flick estén bien calibrados, que iOS
 * respete el `preventDefault` y no se robe el gesto, y que el resorte no se vea
 * brusco. Aquí se fijan la mecánica, los umbrales y el cableado.
 */
import { useCallback, useRef } from 'react';
import { IS_APP_BUILD } from './buildTarget';
import { log as diagLog } from './diag/readyChainLog';

/** Franja del borde izquierdo que pertenece al gesto de salida.
 *
 *  ERA 24 (la zona que iOS reserva en su propio gesto de sistema) y el smoke v3
 *  la encontró corta por partida doble: sobre esos 24 px el chasis se comía 12
 *  con su `px-3`, así que el dedo tenía 12 px reales para acertar. El CAPTADOR
 *  de arriba devuelve los 12; este número da el resto del margen.
 *
 *  POR QUÉ 32 Y NO 44. Con el captador, 32 son 32 px REALES desde el filo —
 *  2.7× lo que había. Y es el valor que menos le quita al gesto hermano: en un
 *  iPhone la tarjeta mide ~366 px de ancho, así que COS-38 conserva 334 (el
 *  91%) para nacer, contra 322 (88%) con 44. Si el dedo de Carlos sigue
 *  fallando, no hay que adivinar el siguiente número: `NAV.SWIPEBACK.MISS`
 *  registra el `xInicial` de cada intento muerto hasta WATCH_ZONE_PX, y el
 *  visor dirá si los fallos caen entre 32 y 60 (subirlo) o antes de 32 (no es
 *  la franja). */
export const EDGE_ZONE_PX = 32;

/** Franja de VIGILANCIA del diagnóstico. Un `touchstart` que nace aquí dentro y
 *  no termina en commit deja constancia en el visor (`NAV.SWIPEBACK.MISS`) con
 *  dónde nació y por qué murió. Es generosa a propósito: cubre la franja del
 *  gesto y el margen de error de un dedo que apuntaba al filo. Fuera de ella no
 *  se registra nada — un toque en el centro de la pantalla no es un intento
 *  fallido de salir, y llenaría el registro de ruido. */
export const WATCH_ZONE_PX = 60;

/** Antes de este recorrido no hay gesto: es el "slop" con el que un toque
 *  todavía cuenta como tap (el mismo orden que el de la píldora, COS-28). Por
 *  debajo de él, en la tarjeta, sigue mandando el volteo. */
export const DIRECTION_SLOP_PX = 10;

/** Fracción del ancho de la vista que hay que recorrer para que el gesto de
 *  salida cuente como decidido. */
export const COMMIT_RATIO = 0.35;

/** Un flick: rápido y corto. Se acepta con la velocidad de la ÚLTIMA muestra
 *  (px/ms) y un recorrido mínimo, para que un temblor veloz no dispare nada. */
export const FLICK_VELOCITY_PX_MS = 0.5;
export const FLICK_MIN_PX = 40;

/** El resorte y el deslizamiento de salida. Sólo `transform`.
 *
 *  TANDA I (B4) — CALIBRACIÓN FIRMADA: 220 → 340 ms. La queja de Carlos es que el
 *  cambio de pantalla al commitear se sentía "golpeado": la vista no se veía
 *  salir, aparecía ya afuera. El número firmado es 340 para TODAS las superficies
 *  de gesto, con curva de frenado — `ease-out` ya era la curva de este archivo
 *  (arranca decidido y aterriza suave), así que la calibración es sólo del
 *  reloj. Los umbrales de disparo NO se tocan: la queja era la animación, no
 *  cuándo dispara.
 *
 *  TANDA N (N2) — RECALIBRACIÓN FIRMADA EN APARATO: 340 → 300 ms. Los 340 se
 *  probaron en el iPhone de Carlos y quedaron LARGOS. 300 REEMPLAZA a 340 en las
 *  TRES superficies (gesto de salida, pestañas y `useCardSwipe`, que lee
 *  `SPRING_CSS` de este mismo archivo). Todo lo demás queda intacto: la curva
 *  sigue siendo `ease-out`, `SETTLE_GUARD_MS` sigue en 820 —el colchón sólo
 *  ganó margen— y los nueve umbrales de disparo no se mueven. El reloj del
 *  EXAMEN tampoco se entera: `onNext`/`onPrev` siguen disparando síncronos, sin
 *  temporizador de por medio (ver el bloque de `useCardSwipe`). */
export const SPRING_MS = 300;
export const SPRING_CSS = `transform ${SPRING_MS}ms ease-out`;

/** Red de seguridad: si tras un commit la pantalla NO se desmonta (un handler
 *  lento, un guardado que tarda), el transform se limpia solo. Una vista no se
 *  queda fuera de pantalla por culpa de este archivo.
 *
 *  TANDA I (B4): 700 → 820 firmado. El colchón anti-doble-gesto tiene que quedar
 *  POR ENCIMA de la animación más larga; con el commit ahora en 340, 700 seguía
 *  alcanzando pero el margen se había comido casi la mitad. 820 lo restituye. */
export const SETTLE_GUARD_MS = 820;

/** Recorrido mínimo para cambiar de tarjeta (COS-38). Es un gesto de contenido,
 *  no de salida: no arrastra la vista, así que no se mide en fracción de ancho. */
export const CARD_COMMIT_PX = 60;

/* LAS FUENTES EXCLUIDAS DEL SWIPE DE PREGUNTA — el censo de la Tanda G, en un
   selector (Encargo 3, punto 5). Es lo que hay DENTRO de una pregunta del examen
   (`exam-content-zone`, ExamSimulator.js) que se arrastra en horizontal por su
   cuenta, censado leyendo el subárbol entero:

     · Badge de dificultad y nombre del tema — estáticos; el tema lleva
       `truncate`, que es `overflow: hidden`: no scrollea.
     · El enunciado (`<MathText>`) y las CUATRO opciones (`<button>` con
       `<MathText>`) — texto que envuelve (`break-words`), tampoco scrollea…
     · …SALVO `.katex-display-wrap`, que declara `overflow-x: auto`
       (MathText.css:7-12) justo para que una ecuación larga se recorra en el
       teléfono en vez de romper la caja. ÉSA es la única fuente de arrastre
       horizontal legítima dentro de una pregunta, y sin excluirla el swipe le
       robaría el dedo: el candado de dirección la vería horizontal, se
       declararía dueño y haría `preventDefault` sobre su scroll nativo.
     · NO hay inputs, ni `input[type="range"]`, ni sliders dentro de la pregunta:
       los dos sliders del examen (`.exam-slider-tap`) viven en la pantalla de
       CONFIGURACIÓN (StudyCenter.js), no aquí, y la hoja de salto es un `Sheet`
       que se monta FUERA de esta caja. Van igual en el selector —junto con
       `[role="slider"]` de Radix— por la misma razón que en el swipe de
       pestañas (COS-I): el día que alguien meta uno en la pregunta no debe hacer
       falta acordarse de este archivo.

   Se mide con `closest` sobre el target del `touchstart`: lo que importa es
   dónde NACE el dedo, no por dónde pasa. */
export const QUESTION_SWIPE_EXCLUDED_SOURCES = '.katex-display-wrap, [role="slider"], input[type="range"], .exam-slider-tap';

// El objeto que ve la web: uno solo, congelado y de identidad estable, para que
// esparcirlo en el contenedor no añada ni una propiedad ni cambie referencias
// entre renders.
const NO_PROPS = Object.freeze({});

/** Reloj del gesto. Los eventos táctiles traen `timeStamp` monotónico; si el
 *  entorno no lo llenara, se cae al reloj de pared. Sólo se usa para velocidad. */
function stampOf(e) {
  const ts = e && e.timeStamp;
  return typeof ts === 'number' && ts > 0 ? ts : Date.now();
}

/** El ancho contra el que se mide el umbral: el del propio contenedor, y si el
 *  entorno no sabe medir (jsdom no tiene layout), el del viewport. */
function widthOf(node) {
  let w = 0;
  try { w = node.getBoundingClientRect().width || 0; } catch (e) { w = 0; }
  if (w > 0) return w;
  try { return window.innerWidth || 0; } catch (e) { return 0; }
}

function logSwipe(pantalla, commit) {
  try {
    diagLog('NAV.SWIPEBACK', { pantalla, commit: !!commit });
  } catch (e) { /* el visor jamás decide un gesto */ }
}

/** Los motivos por los que un dedo que apuntaba al borde NO terminó en commit.
 *  Son los que el código DE VERDAD distingue, ni uno más:
 *    fuera-de-zona       nació entre EDGE_ZONE_PX y WATCH_ZONE_PX: apuntó al
 *                        filo y no llegó. Es el que dice si la franja quedó
 *                        corta.
 *    apagado             nació DENTRO de la franja, pero el gesto estaba
 *                        deshabilitado (una capa encima: modal, prompt,
 *                        onboarding). El dedo era de la capa.
 *    capturado-por-otro  el candado de dirección lo soltó: el movimiento
 *                        dominante fue VERTICAL y el dedo se le dejó al scroll.
 *    umbral-no-alcanzado se soltó sin llegar: o nunca se declaró horizontal, o
 *                        se declaró y el recorrido no bastó (resorte).
 *    interrumpido        otro dedo (pinch) o un `touchcancel` del sistema se
 *                        llevaron el intento.
 */
export const MISS_REASONS = Object.freeze({
  FUERA: 'fuera-de-zona',
  APAGADO: 'apagado',
  OTRO: 'capturado-por-otro',
  UMBRAL: 'umbral-no-alcanzado',
  INTERRUMPIDO: 'interrumpido',
});

/** LA CONSTANCIA DEL INTENTO MUERTO — instrumentación PURA (REMATE-B).
 *
 *  Escucha PASIVA y nada más: se llama desde donde el gesto YA decidió, nunca
 *  antes; no cancela, no marca, no guarda estado que el gesto lea. Si se
 *  borraran las tres llamadas, el gesto se comportaría exactamente igual.
 *  Código HERMANO de `NAV.SWIPEBACK`, no el mismo: aquella línea significa "un
 *  gesto se soltó" y hay cinco baterías que la leen así; ésta significa "un
 *  dedo apuntó al borde y no llegó", que es la pregunta que el visor no podía
 *  contestar. Un resorte deja las DOS (`commit:false` + el porqué con
 *  coordenadas). */
function logMiss(pantalla, motivo, x, y) {
  try {
    diagLog('NAV.SWIPEBACK.MISS', {
      pantalla, motivo, xInicial: Math.round(x), y: Math.round(y),
    });
  } catch (e) { /* el visor jamás decide un gesto */ }
}

/** El nodo donde se escucha. Con `ampliado`, el ancestro scrolleable del chasis
 *  (ver EL CAPTADOR arriba); sin él —o si no hay `<main>`, que es el caso de un
 *  montaje suelto— el propio contenedor, como siempre. */
function captadorDe(node, ampliado) {
  if (!ampliado) return node;
  try { return node.closest('main') || node; } catch (e) { return node; }
}

/**
 * Ref de callback que engancha los cuatro listeners táctiles EN EL CAPTADOR y
 * los suelta cuando el nodo se va (cambio de pantalla o desmontaje). Los
 * envoltorios dereferencian en el momento de la llamada, así que la identidad de
 * la ref es estable para siempre: React no re-engancha en cada render.
 *
 * El NODO viaja a los handlers como argumento (ya no se lee `e.currentTarget`):
 * con el captador ampliado, `currentTarget` es el `<main>` y lo que hay que
 * medir y pintar sigue siendo el contenedor de la pantalla.
 */
function useTouchListeners(handlersRef, ampliado = false) {
  const detachRef = useRef(null);
  return useCallback((node) => {
    if (detachRef.current) { detachRef.current(); detachRef.current = null; }
    if (!node) return;
    const host = captadorDe(node, ampliado);
    const start = (e) => handlersRef.current.start(e, node);
    const move = (e) => handlersRef.current.move(e);
    const end = (e) => handlersRef.current.end(e);
    const cancel = (e) => handlersRef.current.cancel(e);
    // No pasivos los dos primeros: ver el contrato de arriba (WebKit #163207).
    host.addEventListener('touchstart', start, { passive: false });
    host.addEventListener('touchmove', move, { passive: false });
    host.addEventListener('touchend', end);
    host.addEventListener('touchcancel', cancel);
    detachRef.current = () => {
      host.removeEventListener('touchstart', start);
      host.removeEventListener('touchmove', move);
      host.removeEventListener('touchend', end);
      host.removeEventListener('touchcancel', cancel);
      handlersRef.current.detach(node);
    };
  }, [handlersRef, ampliado]);
}

/**
 * useEdgeSwipeBack — el gesto de salida de UNA pantalla.
 *
 * @param {object}   opts
 * @param {string}   opts.screen    nombre de la pantalla para el visor.
 * @param {function} opts.onCommit  EL MISMO handler que el botón de salir.
 * @param {boolean} [opts.enabled]  falso mientras haya un modal encima: no nacen
 *                                  gestos nuevos (uno en vuelo termina en paz).
 * @returns {{ref?: function, onClickCapture?: function}} props para el
 *          CONTENEDOR de la pantalla. En web, `{}`.
 */
export function useEdgeSwipeBack({ screen, onCommit, enabled = true }) {
  const optsRef = useRef(null);
  optsRef.current = { screen, onCommit, enabled };

  const stRef = useRef(null);        // gesto en vuelo, o null
  const guardRef = useRef(null);     // timer de la red de seguridad
  const swallowRef = useRef(false);  // hay un click de compatibilidad que tragar
  // SÓLO DIAGNÓSTICO: dónde nació el intento que sigue vivo, para poder contar
  // cómo murió. El gesto nunca lo lee.
  const missRef = useRef(null);

  const handlersRef = useRef(null);
  if (!handlersRef.current) {
    const clearGuard = () => {
      if (guardRef.current) { clearTimeout(guardRef.current); guardRef.current = null; }
    };
    const paint = (node, x, animado) => {
      node.style.transition = animado ? SPRING_CSS : 'none';
      // Cadena vacía = vuelve a la identidad, y el navegador interpola desde
      // donde esté. Es el resorte, sin dejar transform residual en el nodo.
      node.style.transform = x === 0 ? '' : `translateX(${x}px)`;
    };
    // El intento vivo murió: se apunta y se olvida. Sin intento vivo no hace
    // nada (un toque lejos del borde nunca abrió uno).
    const cerrarIntento = (motivo) => {
      const m = missRef.current;
      missRef.current = null;
      if (m) logMiss(optsRef.current.screen, motivo, m.x, m.y);
    };
    handlersRef.current = {
      start(e, node) {
        const o = optsRef.current;
        // Un dedo nuevo sobre un gesto en vuelo (el segundo de un pinch) lo
        // CANCELA como si el sistema se lo hubiera llevado: resorte y línea. Sin
        // esto la vista se quedaría a medio camino.
        const prev = stRef.current;
        stRef.current = null;
        // Un toque nuevo empieza limpio: nada pendiente de tragar de un gesto
        // anterior (mismo criterio que la píldora, MobileBottomNav).
        swallowRef.current = false;
        if (prev && prev.active) { logSwipe(o.screen, false); paint(prev.node, 0, true); }
        // Lo que hubiera pendiente, murió aquí: llegó otro dedo.
        cerrarIntento(MISS_REASONS.INTERRUMPIDO);
        // LA CONSTANCIA VA ANTES QUE LOS GUARDS, no después: justamente los
        // casos en que el gesto se retira son los que el visor tiene que contar.
        // Nada de esto toca el evento ni el estado del gesto.
        const touches = e.touches;
        const t = touches && touches.length === 1 ? touches[0] : null;
        const primero = touches && touches.length ? touches[0] : null;
        const vigilado = primero && primero.clientX <= WATCH_ZONE_PX;
        if (!o.enabled) {
          if (vigilado) logMiss(o.screen, MISS_REASONS.APAGADO, primero.clientX, primero.clientY);
          return;
        }
        if (!t) {                                       // pinch: no es lo nuestro
          if (vigilado) logMiss(o.screen, MISS_REASONS.INTERRUMPIDO, primero.clientX, primero.clientY);
          return;
        }
        // LA FRANJA. Un toque nacido fuera de ella no es este gesto, y aquí se
        // acaba: ni estado, ni listeners extra, ni preventDefault.
        if (t.clientX > EDGE_ZONE_PX) {
          if (vigilado) logMiss(o.screen, MISS_REASONS.FUERA, t.clientX, t.clientY);
          return;
        }
        clearGuard();
        missRef.current = { x: t.clientX, y: t.clientY };
        stRef.current = {
          node,
          x0: t.clientX, y0: t.clientY,
          lastX: t.clientX, lastT: stampOf(e),
          vx: 0, dx: 0, active: false,
          width: widthOf(node),
        };
      },
      move(e) {
        const st = stRef.current;
        if (!st) return;
        const touches = e.touches;
        if (!touches || touches.length !== 1) {
          stRef.current = null;
          cerrarIntento(MISS_REASONS.INTERRUMPIDO);
          return;
        }
        const t = touches[0];
        const dx = t.clientX - st.x0;
        const dy = t.clientY - st.y0;
        if (!st.active) {
          // CANDADO DE DIRECCIÓN. Vertical dominante (o hacia la izquierda) →
          // el dedo es del scroll y este gesto se retira sin tocar nada.
          if (Math.abs(dy) > DIRECTION_SLOP_PX && Math.abs(dy) >= Math.abs(dx)) {
            stRef.current = null;
            cerrarIntento(MISS_REASONS.OTRO);
            return;
          }
          if (dx <= DIRECTION_SLOP_PX || dx <= Math.abs(dy)) return;
          st.active = true;
        }
        // Ya es nuestro: se cancela el scroll para que la vista siga al dedo.
        if (e.cancelable) e.preventDefault();
        const now = stampOf(e);
        const dt = now - st.lastT;
        if (dt > 0) st.vx = (t.clientX - st.lastX) / dt;
        st.lastX = t.clientX;
        st.lastT = now;
        st.dx = dx > 0 ? dx : 0;
        paint(st.node, st.dx, false);
      },
      end(e) {
        const st = stRef.current;
        stRef.current = null;
        if (!st || !st.active) {
          // Se apoyó en el borde y se levantó sin arrastrar lo suficiente: el
          // caso que el registro no sabía contar.
          cerrarIntento(MISS_REASONS.UMBRAL);
          return;
        }
        const o = optsRef.current;
        const umbral = st.width * COMMIT_RATIO;
        const flick = st.vx >= FLICK_VELOCITY_PX_MS && st.dx >= FLICK_MIN_PX;
        const commit = st.dx >= umbral || flick;
        // Hubo arrastre: el click de compatibilidad que el navegador pueda
        // mandar al soltar ya no representa lo que el usuario quiso (mismo
        // criterio que la píldora, MobileBottomNav).
        swallowRef.current = true;
        logSwipe(o.screen, commit);
        if (!commit) {
          // El resorte deja las DOS líneas: la de siempre (un gesto se soltó sin
          // commit) y el porqué CON coordenadas, que es lo que el visor no tenía.
          cerrarIntento(MISS_REASONS.UMBRAL);
          paint(st.node, 0, true);      // resorte: vuelve y no pasa nada
          return;
        }
        missRef.current = null;         // llegó: no hay intento muerto que contar
        // La vista termina de salir mientras el handler hace lo suyo. Si la
        // pantalla no se desmontara, la red de seguridad la devuelve.
        paint(st.node, st.width, true);
        guardRef.current = setTimeout(() => {
          guardRef.current = null;
          paint(st.node, 0, true);
        }, SETTLE_GUARD_MS);
        // EL MISMO HANDLER QUE EL BOTÓN. Aquí no hay navegación propia.
        if (typeof o.onCommit === 'function') o.onCommit();
      },
      cancel() {
        const st = stRef.current;
        stRef.current = null;
        cerrarIntento(MISS_REASONS.INTERRUMPIDO);
        if (!st || !st.active) return;
        logSwipe(optsRef.current.screen, false);
        paint(st.node, 0, true);
      },
      detach(node) {
        clearGuard();
        stRef.current = null;
        swallowRef.current = false;
        missRef.current = null;   // la pantalla se fue: no hay nada que contar
        try { node.style.transform = ''; node.style.transition = ''; } catch (e) { /* noop */ }
      },
    };
  }

  // `true` = captador ampliado: este gesto nace en el filo de la PANTALLA, no
  // dentro de una caja de contenido.
  const ref = useTouchListeners(handlersRef, true);

  // Un solo click tragado, y sólo si de verdad hubo arrastre.
  const onClickCapture = useCallback((e) => {
    if (!swallowRef.current) return;
    swallowRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  if (!IS_APP_BUILD) return NO_PROPS;
  return { ref, onClickCapture };
}

/**
 * useCardSwipe — COS-38: cambiar de tarjeta con el dedo SOBRE la tarjeta.
 *
 * Hermano del de arriba y con las mismas reglas de convivencia, en el orden en
 * que las aplica:
 *   · la FRANJA DEL BORDE está excluida: un toque nacido ahí es del gesto de
 *     salida y jamás cambia de tarjeta;
 *   · candado de dirección: vertical dominante → el dedo se le deja al scroll
 *     de las caras (el fix pendiente de COS-33/H-1 no se toca ni de lado);
 *   · por debajo de `DIRECTION_SLOP_PX` no hay gesto: sigue siendo el tap que
 *     voltea, como hoy.
 *
 * NO arrastra la tarjeta con el dedo a propósito: mover el nodo del volteo
 * significaría meter mano en el contexto 3D (`preserve-3d`) que ya tiene un
 * diagnóstico abierto. Es un gesto discreto — al soltar, cambia o no cambia.
 *
 * TANDA I (delta, FIRMA 3) — EL CORTE SECO GANA ANIMACIÓN. Éste era el único
 * gesto de la casa que conmutaba SIN ANIMAR: al soltar, la tarjeta (o la
 * pregunta) se cambiaba de golpe. Ahora la que entra ATERRIZA con el mismo reloj
 * y la misma curva firmados para toda la tanda — `SPRING_CSS`, 340 ms `ease-out`,
 * la constante de este mismo archivo, no una copia.
 *
 * POR QUÉ ENTRA Y NO SALE (la decisión estructural, dicha en voz alta). El
 * hermano de arriba y el de pestañas animan la SALIDA: pintan la vista fuera y
 * ejecutan el commit DESPUÉS — el de pestañas, con un temporizador del largo de
 * su propia animación. Copiar esa forma aquí metería el cambio de pregunta del
 * EXAMEN detrás de un temporizador de 340 ms, y eso sí toca el examen: un
 * segundo dedo durante la salida cancelaría el avance pendiente —el hermano de
 * pestañas limpia su temporizador en cada toque nuevo— y el índice se quedaría
 * donde estaba tras un gesto que el usuario ya dio por bueno. Este archivo,
 * además, NO importa nada de aquél: son hermanos, no socios (hay una guarda de
 * fuente que lo vigila). Animando la ENTRADA, `onNext`/`onPrev` se llaman
 * EXACTAMENTE en el mismo instante que antes de esta tanda: síncronos dentro del
 * `touchend`, sin un solo temporizador de por medio. El reloj de pared del examen,
 * el registro de respuestas y `deadline_at` no se rozan — no hay ni una línea
 * nueva entre el gesto y el handler, sólo dos escrituras de estilo al lado.
 *
 * LA MECÁNICA, en dos tiempos dentro del mismo `touchend`:
 *   1. la tarjeta se coloca DE GOLPE (`transition:none`) del lado por el que
 *      viene —swipe a la izquierda = la nueva entra desde la derecha—;
 *   2. un reflow forzado fija ese punto de partida (sin él el navegador colapsa
 *      los dos estilos en uno y no hay transición que correr) y la tarjeta
 *      aterriza en el centro con `SPRING_CSS`.
 * El contenido lo cambia React en el microtask siguiente, ANTES del primer
 * pintado: nadie ve la tarjeta vieja moverse.
 *
 * Y SÓLO SI HAY A DÓNDE IR: en la primera/última tarjeta el handler va nulo y
 * aquí no se anima nada — animar una transición que no ocurre sería mentir.
 *
 * TANDA G — `excludedSources` (opcional). Sin él —el caso de Flashcards, que lo
 * omite— el gesto se comporta EXACTAMENTE como siempre: la guarda de abajo es un
 * `if` sobre `null` y no se llama a `closest` ni una vez. Con él, un toque que
 * NACE sobre un control de arrastre horizontal legítimo jamás es este gesto.
 * Existe porque el swipe de pregunta del examen (Tanda G, Encargo 3) comparte
 * este mecanismo y sus preguntas sí contienen uno (`.katex-display-wrap`).
 *
 * @param {object}   opts
 * @param {function} opts.onNext    EL MISMO handler que la flecha ›
 * @param {function} opts.onPrev    EL MISMO handler que la flecha ‹
 * @param {boolean} [opts.enabled]
 * @param {string}  [opts.excludedSources] selector CSS de fuentes excluidas.
 * @param {boolean} [opts.tambienEnLaWeb] smoke-alumno (S9, orden de Carlos del
 *          20-oct): verdadero en una pantalla TACTIL de la web (lo pasa Flashcards
 *          con el detector de la casa). Entonces el gesto vive tambien fuera de la
 *          app, con ESTE mismo codigo: solo cambia la condicion de abajo. Sin la
 *          opcion (el examen), nada cambia: en web sigue siendo `{}`.
 * @returns {{ref?: function, onClickCapture?: function}} props para el
 *          CONTENEDOR de la tarjeta. En web, `{}` (salvo `tambienEnLaWeb`).
 */
export function useCardSwipe({ onNext, onPrev, enabled = true, excludedSources = null, tambienEnLaWeb = false }) {
  const optsRef = useRef(null);
  optsRef.current = { onNext, onPrev, enabled, excludedSources };

  const stRef = useRef(null);
  const swallowRef = useRef(false);
  const guardRef = useRef(null);     // limpieza de los estilos de la entrada

  const handlersRef = useRef(null);
  if (!handlersRef.current) {
    const clearGuard = () => {
      if (guardRef.current) { clearTimeout(guardRef.current); guardRef.current = null; }
    };
    /* Gemelo del `paint` del hermano de arriba, con su misma regla: SÓLO
       `transform`, jamás una propiedad de layout (la lección de `glass-card` y su
       `transition: all .3s` — y la caja de la pregunta del examen ES una
       `glass-card`). No se reutiliza aquel `paint` porque vive dentro de su
       propio closure y sacarlo de ahí sería un refactor del gesto de salida, que
       en esta tanda no se toca. */
    const paint = (node, x, animado) => {
      node.style.transition = animado ? SPRING_CSS : 'none';
      node.style.transform = x === 0 ? '' : `translateX(${x}px)`;
    };
    /* LA ENTRADA (TANDA I, FIRMA 3). `lado` es +1 si la nueva viene de la derecha
       (el dedo se fue a la izquierda → siguiente) y -1 si viene de la izquierda.
       Los estilos en línea se limpian solos al final: la caja del examen es una
       `glass-card` y dejarle una `transition` propia clavada le taparía la suya
       para siempre. `SETTLE_GUARD_MS` es el colchón de la casa, ya calibrado por
       encima de la animación más larga. */
    const entrar = (node, lado, ancho) => {
      if (!node || !ancho) return;
      clearGuard();
      paint(node, lado * ancho, false);
      // El reflow forzado: fija el punto de partida antes de pedir el aterrizaje.
      try { void node.offsetWidth; } catch (e) { /* jsdom no tiene layout */ }
      paint(node, 0, true);
      guardRef.current = setTimeout(() => {
        guardRef.current = null;
        try { node.style.transition = ''; node.style.transform = ''; } catch (e) { /* noop */ }
      }, SETTLE_GUARD_MS);
    };
    handlersRef.current = {
      start(e, node) {
        const o = optsRef.current;
        stRef.current = null;
        swallowRef.current = false;   // toque nuevo, nada pendiente de tragar
        if (!o.enabled) return;
        const touches = e.touches;
        if (!touches || touches.length !== 1) return;
        // LAS FUENTES EXCLUIDAS (Tanda G, y sólo para quien las declare). Nace
        // sobre un control que se arrastra solo → el dedo es suyo, y aquí se
        // acaba: ni estado, ni preventDefault. Sin selector no se ejecuta nada.
        if (o.excludedSources) {
          const target = e.target;
          try {
            if (target && target.closest && target.closest(o.excludedSources)) return;
          } catch (err) { /* selector imposible en este entorno: no excluye nada */ }
        }
        const t = touches[0];
        // LA FRANJA ES DEL OTRO GESTO. Sin este descarte, un arrastre desde el
        // borde cambiaría de tarjeta Y saldría de la sesión con el mismo dedo.
        if (t.clientX <= EDGE_ZONE_PX) return;
        stRef.current = {
          node,
          x0: t.clientX, y0: t.clientY,
          lastX: t.clientX, lastT: stampOf(e),
          vx: 0, dx: 0, active: false,
          width: widthOf(node),
        };
      },
      move(e) {
        const st = stRef.current;
        if (!st) return;
        const touches = e.touches;
        if (!touches || touches.length !== 1) { stRef.current = null; return; }
        const t = touches[0];
        const dx = t.clientX - st.x0;
        const dy = t.clientY - st.y0;
        if (!st.active) {
          if (Math.abs(dy) > DIRECTION_SLOP_PX && Math.abs(dy) >= Math.abs(dx)) {
            stRef.current = null;      // es scroll de la cara: no es nuestro
            return;
          }
          if (Math.abs(dx) <= DIRECTION_SLOP_PX || Math.abs(dx) <= Math.abs(dy)) return;
          st.active = true;
        }
        if (e.cancelable) e.preventDefault();
        const now = stampOf(e);
        const dt = now - st.lastT;
        if (dt > 0) st.vx = (t.clientX - st.lastX) / dt;
        st.lastX = t.clientX;
        st.lastT = now;
        st.dx = dx;
      },
      end() {
        const st = stRef.current;
        stRef.current = null;
        if (!st || !st.active) return;
        const o = optsRef.current;
        swallowRef.current = true;     // el arrastre no puede acabar volteando
        const recorrido = Math.abs(st.dx);
        const flick = Math.abs(st.vx) >= FLICK_VELOCITY_PX_MS && recorrido >= FLICK_MIN_PX;
        if (recorrido < CARD_COMMIT_PX && !flick) return;
        // LOS MISMOS HANDLERS QUE LAS FLECHAS. Izquierda → siguiente, derecha →
        // anterior. Si la flecha no existe (primera/última tarjeta), la pantalla
        // no pasa handler y aquí no pasa nada — tampoco animación (TANDA I).
        const fn = st.dx < 0 ? o.onNext : o.onPrev;
        if (typeof fn !== 'function') return;
        // TANDA I (FIRMA 3) — la que entra ATERRIZA, y el handler se llama en el
        // MISMO instante que antes de esta tanda: sin temporizador de por medio.
        entrar(st.node, st.dx < 0 ? 1 : -1, st.width);
        fn();
      },
      cancel() { stRef.current = null; },
      detach(node) {
        clearGuard();
        stRef.current = null;
        swallowRef.current = false;
        try { node.style.transform = ''; node.style.transition = ''; } catch (e) { /* noop */ }
      },
    };
  }

  const ref = useTouchListeners(handlersRef);

  const onClickCapture = useCallback((e) => {
    if (!swallowRef.current) return;
    swallowRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  if (!IS_APP_BUILD && !tambienEnLaWeb) return NO_PROPS;
  return { ref, onClickCapture };
}
